import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  authSelector,
  metaSelector,
  softwareListSelector,
  fetchSoftwareList,
} from 'store';
import { SortOrder } from 'utils/array';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Card from 'components/UI/Card';
import FoldableCard from 'components/UI/FoldableCard';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import PageWrapper from 'components/UI/PageWrapper';
import SearchField from 'components/UI/SearchField';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import Loader from 'components/UI/Loader';
import { createSoftwareFilterAttribute } from 'utils/filtering';
import { CardHeaderTop, CardHeaderTopColumn } from './styled';
import CardContainer from 'components/UI/CardContainer';
import SoftwareTable from 'components/software/SoftwareTable';
import { SoftwareFilter, SoftwareList } from 'models/software';
import Button from 'components/UI/Button';
import { setSoftwareFilters } from 'store/softwareState/softwareState';
import SoftwareFilters from 'components/software/SoftwareTable/SoftwareFilter';
// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const SoftwarePage: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { session } = useAppSelector(authSelector);
  const meta = useAppSelector(metaSelector);
  const { data, isLoading, filters, totalCount }: SoftwareList =
    useAppSelector(softwareListSelector);

  useEffect(() => {
    dispatch(fetchSoftwareList([], initialPagination.offset));
  }, [dispatch]);

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Fetch softwares
  const fetchSoftwares = useCallback(
    (softwareFilters: SoftwareFilter[], offset?: number) => {
      dispatch(fetchSoftwareList(softwareFilters, offset ?? 0));
    },
    [dispatch]
  );

  // Search
  const onSearch = useCallback(
    (value: string) => {
      const searchFilters = createSoftwareFilterAttribute(value);
      setPagination(initialPagination);
      if (searchFilters.length) {
        fetchSoftwares(searchFilters, initialPagination.offset);
      }
    },
    [fetchSoftwares]
  );

  const onClear = useCallback(() => {
    setPagination(initialPagination);
    fetchSoftwares([], initialPagination.offset);
  }, [fetchSoftwares]);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      if (filters.data) {
        fetchSoftwares(filters.data, paginate.offset);
      } else {
        fetchSoftwares([], paginate.offset);
      }
    },
    [fetchSoftwares, filters.data]
  );

  // Toggle filters
  const toggleFilters = useCallback(() => {
    dispatch(setSoftwareFilters(!filters.active));
  }, [dispatch, filters.active]);

  // Apply filters
  const onFilter = useCallback(
    (softwareFilters: SoftwareFilter[]) => {
      setPagination(initialPagination);
      fetchSoftwares(softwareFilters, initialPagination.offset);
    },
    [fetchSoftwares]
  );

  // Reset pagination
  const resetPagination = useCallback(
    () => setPagination(initialPagination),
    []
  );

  // Render results
  const results = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!data || data.length === 0) {
      return null;
    }
    return (
      <CardContainer>
        <Card>
          <CardHeaderTop>
            <CardHeaderTopColumn>
              <Heading type={HeadingType.h3} uppercase>
                <FormattedMessage id="software.table_title" />
              </Heading>
            </CardHeaderTopColumn>
            <CardHeaderTopColumn>
              <Button
                marginRight={true}
                onClick={() =>
                  (window.location.href = window.location.href + '/upload')
                }
              >
                <FormattedMessage id="software.upload_btn" />
              </Button>
            </CardHeaderTopColumn>
          </CardHeaderTop>
          <SoftwareTable
            softwares={data}
            initialActiveColumn={2}
            initialSortOrder={SortOrder.Asc}
          />
          <Pagination
            totalCount={totalCount || 0}
            noItems={data.length}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </Card>
      </CardContainer>
    );
  }, [data, isLoading, pagination, totalCount, onSetPagination]);

  return (
    <PageWrapper>
      <CardHeaderTop>
        <Heading>
          <FormattedMessage id="software.title" />
        </Heading>
      </CardHeaderTop>
      <Card>
        <CardHeaderTop></CardHeaderTop>
        <SearchField
          onSearch={onSearch}
          onClear={onClear}
          placeholder={intl.formatMessage({
            id: 'software.search_placeholder',
          })}
        >
          <Button
            onClick={toggleFilters}
            backgroundColor="surface"
            color={filters.active ? 'primaryDark' : 'onSurface'}
          >
            <FormattedMessage id="filters.btn" />
          </Button>
        </SearchField>
      </Card>
      <FoldableCard open={filters.active}>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="software.filter_title" />
        </Heading>
        <SoftwareFilters
          onFilter={onFilter}
          onClear={resetPagination}
          data={meta.data}
          session={session}
        />
      </FoldableCard>
      {results}
    </PageWrapper>
  );
};

export default SoftwarePage;
